import classNames from "classnames";
import { forwardRef, HTMLAttributes } from "react";

import Button from "src/components-v2/Button";
import {
  ModalBody,
  ModalBodyContainer,
  ModalDialog,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  ModalWrapper
} from "./Modal.styled";

import { getTranslationManually } from "src/helpers/locale";
import { PropsWithDataQaid } from "src/types/componentProps";

export type ModalProps = HTMLAttributes<HTMLDivElement> &
  PropsWithDataQaid<{
    /**
     * Set modal title
     */
    title?: string;

    /**
     * Set modal showed or hidden
     * @default {false}
     */
    show?: boolean;

    /**
     * Set modal size
     * @type {"default" | "small" | "large" | "huge" | "semi-small"}
     * @default {"default"}
     */
    size?: "default" | "small" | "large" | "huge" | "semi-small";

    /**
     * Set modal to be hidden when overlay clicked
     * @default {false}
     */
    closeOnOverlay?: boolean;

    /**
     * Set modal has no header
     * @default {false}
     */
    withoutHeader?: boolean;

    /**
     * Set modal has no close button
     * @default {false}
     */
    withoutCloseButton?: boolean;

    /**
     * Function for hiding modal
     */
    onClose: () => void;

    /**
     * Set close icon as "X" instead of text
     */
    closeIcon?: boolean;

    /**
     * Class to set the body of the modal
     */
    modalBodyClass?: string;
  }>;

const defaultProps: ModalProps = {
  title: "",
  show: false,
  size: "default",
  closeOnOverlay: false,
  withoutHeader: false,
  withoutCloseButton: false,
  onClose: () => {},
  closeIcon: false
};

const Modal = forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const {
    children,
    className,
    modalBodyClass,
    title,
    show,
    size,
    id,
    closeOnOverlay,
    withoutHeader,
    withoutCloseButton,
    closeIcon,
    onClose,
    "data-qaid": dataQaid,
    ...restProps
  } = props;

  function handleOverlayClick() {
    if (closeOnOverlay) onClose();
  }

  const modalClass = classNames("c-modal", show ? "c-modal-on" : "c-modal-off", className, "u-px-3 sm:u-p-0");
  const modalDialogClass = classNames("c-modal__dialog", className);

  return (
    <ModalWrapper ref={ref} className={modalClass} data-qaid={dataQaid} data-testid={"qa-modal"} {...restProps}>
      <ModalOverlay className="c-modal__overlay" onClick={handleOverlayClick} data-testid="qa-modal-overlay" />
      <ModalDialog id={id} className={modalDialogClass} size={size} data-testid="qa-modal-dialog">
        {!withoutHeader && (
          <ModalHeader className="c-modal__header" data-testid="qa-modal-header" data-qaid={`${dataQaid}-header`}>
            <ModalTitle data-testid="qa-modal-title">{title}</ModalTitle>
            {!withoutCloseButton &&
              (closeIcon ? (
                <span
                  onClick={onClose}
                  className="u-text-neutral u-text-xl u-p-2 u-cursor-pointer hover:u-text-dark-smoke"
                >
                  &#10005;
                </span>
              ) : (
                <Button variant="tertiary" onClick={onClose} data-testid="qa-modal-close-button">
                  {getTranslationManually("TUTUP", "CLOSE")}
                </Button>
              ))}
          </ModalHeader>
        )}
        <ModalBody className="c-modal__body" data-testid="qa-modal-body" data-qaid={`${dataQaid}-body`}>
          <ModalBodyContainer className={modalBodyClass} data-testid="qa-modal-body-container">
            {children}
          </ModalBodyContainer>
        </ModalBody>
      </ModalDialog>
    </ModalWrapper>
  );
});

Modal.defaultProps = defaultProps;

export default Modal;
